import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { PageTitleService } from '@app/shared/services/page-title.service';
import { StateService } from '@uirouter/angular';
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import CONSTANTS from '@constants';
import { BackgroundBrandedService } from '@app/shared/modules/background-branded/services/background-branded.service';
import { CompanyService } from '@app/shared/services/company.service';
import { fbEmailValidator } from '@app/modules/ui/validators/email-validator';
import { RegistrationService } from '@app/modules/auth/containers/register-new/registration.service';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';
import { AuthService } from '@app/shared/services/auth.service';
import { SsoService } from '@app/shared/services/sso.service';
import { first } from 'rxjs/operators';
import { IFBBrandedRegistrationText } from '@interfaces';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  standalone: false
})
export class RegisterComponent implements OnInit {
  private backgroundBrandedService = inject(BackgroundBrandedService);
  private companyService = inject(CompanyService);
  private registrationService = inject(RegistrationService);
  private pageTitle = inject(PageTitleService);
  private stateService = inject(StateService);
  private translateService = inject(TranslateService);
  private domSanitizer = inject(DomSanitizer);
  private ssoService = inject(SsoService);
  private auth = inject(AuthService);
  private fb = inject(FormBuilder);
  isInProgress = signal(false);
  highlightErrors = signal(false);

  allowedDomainsAsList = this.registrationService.allowedDomainsAsList;
  isDomainBlocked = this.registrationService.isDomainBlocked;

  companyName = signal(this.companyService.data.name);
  cookiesLink = signal(this.translateService.getLocalizedConstantString(CONSTANTS.WEB_LINKS.COOKIES));
  registrationLink = signal({ domains: [''] });
  registrationPageBrandingTexts = signal<IFBBrandedRegistrationText>(null);

  invitationEmailForm = this.fb.group({
    email: new FormControl<string>('', [Validators.required, fbEmailValidator()])
  });

  private loginLink = toSignal(this.auth.LOGIN_LINK);
  private ssoLink = computed(() => this.loginLink() + '/sso');
  loginUrl = computed(() => this.domSanitizer.bypassSecurityTrustUrl(this.loginLink()));

  ngOnInit() {
    this.getBranding();
    this.getRegistrationLink();
    this.getCompany();
    this.ssoService.fetchIdentityProviders().pipe(first()).subscribe();
    this.pageTitle.setTitle('page-titles.register', { company: this.companyName() });
  }

  isInvalidDomain(): boolean {
    return this.isDomainBlocked(this.registrationLink().domains, this.emailControl.value) && this.emailControl.valid;
  }

  hasSsoEnabled(): boolean {
    return this.ssoService.isEnabled;
  }

  get emailControl() {
    return this.invitationEmailForm.controls.email;
  }

  requestRegistrationEmail() {
    if (
      this.isInProgress() ||
      this.isDomainBlocked(this.registrationLink().domains, this.emailControl.value) ||
      this.emailControl.invalid
    ) {
      this.highlightErrors.set(true);
      return;
    }

    this.isInProgress.set(true);

    this.registrationService.requestRegistrationEmail(this.emailControl.value).subscribe(
      () => {
        this.stateService.go('register-email-sent', { email: this.emailControl.value });
      },
      err => {
        this.highlightErrors.set(true);
        if (err.error.code === '4004') {
          this.emailControl.setErrors({ userExists: true });
        } else if (err.error.code === '40037') {
          this.emailControl.setErrors({ emailDisallowed: true });
        } else if (err.error.code === '5030') {
          this.emailControl.setErrors({ serviceUnavailable: true });
        } else {
          this.emailControl.setErrors({ registrationFailed: true });
        }
        this.isInProgress.set(false);
      }
    );
  }

  redirectToSSO() {
    window.location.href = this.ssoLink();
  }

  private getRegistrationLink() {
    this.registrationService
      .getRegistrationLink()
      .pipe(first())
      .subscribe(registrationLink => {
        if (!registrationLink) {
          this.stateService.go('register-blocked', undefined, { location: 'replace' });
        }

        this.registrationLink.set(registrationLink);
      });
  }

  private getBranding() {
    this.backgroundBrandedService
      .fetchBranding()
      .pipe(first())
      .subscribe(() => {
        this.registrationPageBrandingTexts.set(
          this.registrationService.getLocalizedInterpolatedBrandingTexts(
            this.companyService.data.registrationPageBranding.details
          )
        );
      });
  }

  private getCompany() {
    this.registrationService
      .getCompany()
      .pipe(first())
      .subscribe(company => {
        if (!company) {
          this.stateService.go('not-found', { redirectedFrom: this.stateService.transition });
        }
      });
  }
}
